import { Box, Text, VStack, Image } from "@chakra-ui/react";
import { Global, css } from "@emotion/react";
import { useAnimationToggle } from "../Contexts/AnimationContext";
import pinpointLogo from "../assets/pinpoint_no_bg.svg";
import { OUTER_LEN, PIN_LEN, VB, OUTER_D, PIN_D, strokeStyle } from "../assets/pinpointLogoPaths";

const CYCLE = "4s";

// Timeline (% of 4s cycle):
// 0%–25%   stroke draws on (1s)
// 25%–40%  fill rises from bottom (0.6s)
// 40%–55%  hold filled (0.6s)
// 55%–70%  fill drains from top (0.6s)
// 70%–88%  stroke erases (0.72s)
// 88%–100% gap (0.48s)

const animationStyles = css`
  @keyframes drawOuter {
    0% { stroke-dashoffset: ${OUTER_LEN}; }
    25% { stroke-dashoffset: 0; }
    70% { stroke-dashoffset: 0; }
    88% { stroke-dashoffset: ${-OUTER_LEN}; }
    100% { stroke-dashoffset: ${-OUTER_LEN}; }
  }

  @keyframes drawPin {
    0% { stroke-dashoffset: ${PIN_LEN}; }
    25% { stroke-dashoffset: 0; }
    70% { stroke-dashoffset: 0; }
    88% { stroke-dashoffset: ${-PIN_LEN}; }
    100% { stroke-dashoffset: ${-PIN_LEN}; }
  }

  @keyframes fillRise {
    0% { transform: translateY(100%); }
    10% { transform: translateY(100%); }
    27% { transform: translateY(0%); }
    55% { transform: translateY(0%); }
    70% { transform: translateY(100%); }
    100% { transform: translateY(100%); }
  }
`;

function KioskLoadingAnimation() {
  const { animationsEnabled } = useAnimationToggle();

  if (!animationsEnabled) {
    return (
      <VStack spacing={3}>
        <Image src={pinpointLogo} alt="Pinpoint" boxSize={10} />
        <Text fontSize="xl" color="gray.400" fontWeight={500}>
          Processing...
        </Text>
      </VStack>
    );
  }

  return (
    <>
      <Global styles={animationStyles} />
      <Box w="120px" h="120px">
        <svg viewBox="0 0 512 512" width="100%" height="100%">
          <defs>
            <linearGradient id="loading-grad-outer" x1="131.26" y1="172.25" x2="380.74" y2="172.25" gradientUnits="userSpaceOnUse">
              <stop offset="0" stopColor="#EC2227" />
              <stop offset="1" stopColor="#F15B27" />
            </linearGradient>
            <linearGradient id="loading-grad-pin" x1="131.88" y1="380.34" x2="241.58" y2="380.34" gradientUnits="userSpaceOnUse">
              <stop offset="0" stopColor="#EC2227" />
              <stop offset="1" stopColor="#F15B27" />
            </linearGradient>

            <clipPath id="loading-fill-clip">
              <rect
                x="0" y="0" width={VB} height={VB}
                style={{
                  animation: `fillRise ${CYCLE} ease-in-out infinite`,
                }}
              />
            </clipPath>
          </defs>

          {/* Layer 1: Stroked outlines (no fill) */}
          <path
            d={OUTER_D}
            stroke="url(#loading-grad-outer)"
            style={{
              ...strokeStyle,
              strokeDasharray: OUTER_LEN,
              strokeDashoffset: OUTER_LEN,
              animation: `drawOuter ${CYCLE} ease-in-out infinite`,
            }}
          />
          <path
            d={PIN_D}
            stroke="url(#loading-grad-pin)"
            style={{
              ...strokeStyle,
              strokeDasharray: PIN_LEN,
              strokeDashoffset: PIN_LEN,
              animation: `drawPin ${CYCLE} ease-in-out infinite`,
            }}
          />

          {/* Layer 2: Filled shapes clipped by animated rect */}
          <g clipPath="url(#loading-fill-clip)">
            <path d={OUTER_D} fill="url(#loading-grad-outer)" stroke="none" />
            <path d={PIN_D} fill="url(#loading-grad-pin)" stroke="none" />
          </g>
        </svg>
      </Box>
    </>
  );
}

export default KioskLoadingAnimation;
